import React, { useState, useEffect } from "react";
import CarouselSection from "../CarouselSection/CarouselSection";
import Header from "../FleetHeader/Header";
import { cars } from '../../services/api';
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";

const LuxuryFleet = () => {
  const [filters, setFilters] = useState({
    jeepSUV: [],
    miniVans: [],
    cabrioSpecial: [],
    jeepWrangler: []
  });

  const [luxuryCarData, setLuxuryCarData] = useState({
    jeepSUV: [],
    miniVans: [],
    cabrioSpecial: [],
    jeepWrangler: []
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    const fetchCars = async () => {
      try {
        const response = await cars.getAll();
        const today = new Date();
        
        // Get today's date for availability check
        const todayStr = today.toISOString();
        
        // Fetch current price for all cars in one request
        const availabilityResponse = await cars.getAvailable({
          startDate: todayStr,
          endDate: todayStr,
          includeAll: true
        });
        
        // Create a map of car IDs to dynamic prices
        const dynamicPriceMap = {};
        availabilityResponse.data.forEach(car => {
          dynamicPriceMap[car._id] = car.dynamicPrice;
        });
        
        // Filter cars to only show active/available ones
        const activeCars = response.data.filter(car => 
          car.status === 'available' || car.status === 'rented' || !car.status // Include cars without status field for backward compatibility
        );
        
        const allCars = activeCars.map(car => ({
          ...car,
          name: `${car.make} ${car.model} ${car.transmission.charAt(0).toUpperCase() + car.transmission.slice(1)} (or Similar)`,
          image: car.images[0],
          dynamicPrice: dynamicPriceMap[car._id] || car.basePrice // Use dynamic price if available, otherwise use base price
        }));
        
        // Group cars by category
        const grouped = {
          jeepSUV: allCars.filter(car => car.category === 'Jeep-SUV-Family'),
          miniVans: allCars.filter(car => car.category === 'Mini Vans'),
          cabrioSpecial: [
            ...allCars.filter(car => car.category === 'Cabrio'),
            ...allCars.filter(car => car.category === 'Special')
          ],
          jeepWrangler: allCars.filter(car => car.category === 'Jeep Wrangler')
        };

        setLuxuryCarData(grouped);
      } catch (err) {
        console.error('Error fetching cars:', err);
        setError('Failed to load cars. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchCars();
  }, []);

  const filterCars = (cars, selectedFilters) => {
    if (!selectedFilters || selectedFilters.length === 0) return cars;

    return cars.filter((car) => {
      return selectedFilters.every((filter) => {
        if (filter.label.includes("Person")) {
          return car.persons === filter.value;
        }
        if (filter.label.includes("Suitcase")) {
          return car.suitcases === filter.value;
        }
        if (filter.value === "Manual" || filter.value === "Automatic") {
          return car.transmission.toLowerCase() === filter.value.toLowerCase();
        }
        return true;
      });
    });
  };

  const sliderRefs = {
    jeepSUV: React.useRef(),
    miniVans: React.useRef(),
    cabrioSpecial: React.useRef(),
        jeepWrangler: React.useRef()
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div className="text-center text-red-500 py-8">{error}</div>;
  }

  return (
    <>
      <section className="py-10 bg-gray-50">
        <div className="items-center mx-auto container">
          <h2 className="text-4xl mb-4 text-center font-medium Aleo">
            Luxury & Special <b>Car</b> Selection
          </h2>
          <hr className="w-40 border-2 border-red-400 mx-auto mb-4" />
          <p className="text-gray-400 text-sm max-w-2xl mx-auto text-center Aleo">
            Experience Mykonos in style with our premium selection of vehicles.
            From spacious family SUVs to elegant convertibles, we have the
            perfect luxury car for your journey.
          </p>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-8">
      <div className="mb-12">
          <Header
            title="Jeep-Wrangler"
            link="/Jeep-Wrangler"
            onNext={() => sliderRefs.jeepWrangler.current.slickNext()}
            onPrev={() => sliderRefs.jeepWrangler.current.slickPrev()}
            cars={luxuryCarData.jeepWrangler}
            onFilterChange={(selected) =>
              setFilters((prev) => ({ ...prev, jeepWrangler: selected }))
            }
          />
          <CarouselSection
            cars={filterCars(
              luxuryCarData.jeepWrangler,
              filters.jeepWrangler
            )}
            sliderRef={sliderRefs.jeepWrangler}
          />
        </div>
        <div className="mb-12">
          <Header
            title="Jeep & SUV Family"
            link="/jeep-suv"
            onNext={() => sliderRefs.jeepSUV.current.slickNext()}
            onPrev={() => sliderRefs.jeepSUV.current.slickPrev()}
            cars={luxuryCarData.jeepSUV}
            onFilterChange={(selected) =>
              setFilters((prev) => ({ ...prev, jeepSUV: selected }))
            }
          />
          <CarouselSection
            cars={filterCars(luxuryCarData.jeepSUV, filters.jeepSUV)}
            sliderRef={sliderRefs.jeepSUV}
          />
        </div>

        <div className="mb-12">
          <Header
            title="Mini Vans"
            link="/mini-vans"
            onNext={() => sliderRefs.miniVans.current.slickNext()}
            onPrev={() => sliderRefs.miniVans.current.slickPrev()}
            cars={luxuryCarData.miniVans}
            onFilterChange={(selected) =>
              setFilters((prev) => ({ ...prev, miniVans: selected }))
            }
          />
          <CarouselSection
            cars={filterCars(luxuryCarData.miniVans, filters.miniVans)}
            sliderRef={sliderRefs.miniVans}
          />
        </div>

        <div className="mb-12">
          <Header
            title="Cabrio-Special"
            link="/Cabrio-Special"
            onNext={() => sliderRefs.cabrioSpecial.current.slickNext()}
            onPrev={() => sliderRefs.cabrioSpecial.current.slickPrev()}
            cars={luxuryCarData.cabrioSpecial}
            onFilterChange={(selected) =>
              setFilters((prev) => ({ ...prev, cabrioSpecial: selected }))
            }
          />
          <CarouselSection
            cars={filterCars(
              luxuryCarData.cabrioSpecial,
              filters.cabrioSpecial
            )}
            sliderRef={sliderRefs.cabrioSpecial}
          />
        </div>
      
      </section>
    </>
  );
};

export default LuxuryFleet;
